import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Game, Player, Episode } from '../types';

const API_URL = process.env.REACT_APP_API_URL || '';

const GamePlay: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [showEliminateModal, setShowEliminateModal] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState<Episode | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchGame();
  }, [gameId]);

  const fetchGame = async () => {
    try {
      const res = await fetch(`${API_URL}/api/games/${gameId}`);
      const data = await res.json();
      setGame(data);
    } catch (error) {
      console.error('Error fetching game:', error);
    }
  };

  const addEpisode = async () => {
    if (!game) return;
    try {
      await fetch(`${API_URL}/api/games/${game.id}/episodes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `Aflevering ${game.episodes.length + 1}`
        })
      });
      fetchGame();
    } catch (error) {
      console.error('Error adding episode:', error);
    }
  };

  const eliminatePlayer = async (playerId: string) => {
    if (!game || !selectedEpisode) return;
    try {
      await fetch(`${API_URL}/api/games/${game.id}/episodes/${selectedEpisode.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eliminatedPlayerId: playerId })
      });
      setShowEliminateModal(false);
      setSelectedEpisode(null);
      fetchGame();
    } catch (error) {
      console.error('Error eliminating player:', error);
    }
  };

  const getActivePlayers = () => {
    if (!game) return [];
    return game.players.filter(p => !p.isEliminated);
  };

  const getPlayerById = (id: string | null) => {
    if (!game || !id) return null;
    return game.players.find(p => p.id === id);
  };

  if (!game) {
    return <div className="page loading">Laden...</div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>{game.name}</h1>
        <p>{getActivePlayers().length} kandidaten nog in de race</p>
      </div>

      <div className="nav-bar">
        <Link to={`/game/${gameId}/vote`} className="btn btn-accent">
          Stemmen
        </Link>
        <Link to={`/game/${gameId}/scoreboard`} className="btn btn-secondary">
          Scorebord
        </Link>
        <Link to={`/game/${gameId}/admin`} className="btn btn-secondary">
          Admin
        </Link>
      </div>

      {/* Players */}
      <div className="card">
        <h2>Kandidaten</h2>
        <ul className="player-list">
          {game.players.map((player: Player) => (
            <li key={player.id} className="player-item">
              <div className="player-info">
                <div 
                  className="player-avatar"
                  style={{ 
                    backgroundColor: player.color,
                    opacity: player.isEliminated ? 0.5 : 1
                  }}
                >
                  {player.name.charAt(0).toUpperCase()}
                </div>
                <span className={`player-name ${player.isEliminated ? 'eliminated' : ''}`}>
                  {player.name}
                </span>
              </div>
              {player.isEliminated && (
                <span style={{ color: 'var(--mol-red)', fontSize: '0.9rem' }}>
                  Afgevallen (Afl. {player.eliminatedInEpisode})
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Episodes */}
      <div className="card">
        <div className="card-header">
          <h2>Afleveringen</h2>
          <button className="btn btn-primary btn-small" onClick={addEpisode}>
            + Aflevering
          </button>
        </div>

        {game.episodes.length === 0 ? (
          <div className="empty-state">
            <p>Nog geen afleveringen</p>
          </div>
        ) : (
          <div>
            {game.episodes.map((episode: Episode) => {
              const eliminated = getPlayerById(episode.eliminatedPlayerId);
              return (
                <div key={episode.id} className="episode-item">
                  <div className="episode-number">{episode.number}</div>
                  <div className="episode-info">
                    <div className="episode-title">{episode.title}</div>
                    {eliminated ? (
                      <div className="episode-eliminated">
                        Afgevallen: {eliminated.name}
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.9rem', color: 'var(--mol-text-muted)' }}>
                        Nog geen afvaller
                      </div>
                    )}
                  </div>
                  {!episode.eliminatedPlayerId && (
                    <button 
                      className="btn btn-danger btn-small"
                      onClick={() => {
                        setSelectedEpisode(episode);
                        setShowEliminateModal(true);
                      }}
                    >
                      Afvaller
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button 
        className="btn btn-secondary btn-block" 
        onClick={() => navigate('/')}
        style={{ marginTop: '20px' }}
      >
        ← Terug naar overzicht
      </button>

      {/* Eliminate Modal */}
      {showEliminateModal && selectedEpisode && (
        <div className="modal-overlay" onClick={() => setShowEliminateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Wie is afgevallen?</h2>
            <p style={{ color: 'var(--mol-text-muted)', marginBottom: '20px' }}>
              Selecteer de kandidaat die is afgevallen in {selectedEpisode.title}
            </p>
            <div className="vote-grid">
              {getActivePlayers().map((player: Player) => (
                <div 
                  key={player.id}
                  className="vote-card"
                  onClick={() => eliminatePlayer(player.id)}
                >
                  <div 
                    className="vote-avatar"
                    style={{ backgroundColor: player.color }}
                  >
                    {player.name.charAt(0).toUpperCase()}
                  </div>
                  <div>{player.name}</div>
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary btn-block"
                onClick={() => setShowEliminateModal(false)}
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamePlay;
